import { Button } from "@heroui/react";
import { formatDistanceToNow } from "date-fns";
import React from "react";

import { ReactComponent as CupIcon } from "@wc/ui/assets/icons/cup.svg";
import { ReactComponent as LockIcon } from "@wc/ui/assets/icons/lock.svg";
import { ReactComponent as QuoteDownCircleIcon } from "@wc/ui/assets/icons/quote-down-circle.svg";
import { ReactComponent as StopCircleIcon } from "@wc/ui/assets/icons/stop-circle.svg";
import { ReactComponent as TickCircleIcon } from "@wc/ui/assets/icons/tick-circle.svg";
import { ReactComponent as UnlockIcon } from "@wc/ui/assets/icons/unlock.svg";

import CommentInput from "./comment-input";
import EventReactions from "./event-reactions";

const ITEMS_PER_PAGE = 5;

/**
 * Event style definitions - written as literals for Tailwind scanning
 */
const EVENT_STYLES = {
  goal_checked: {
    icon: TickCircleIcon,
    colorClass: "text-success",
    dotColor: "bg-success",
  },
  goal_unchecked: {
    icon: StopCircleIcon,
    colorClass: "text-warning",
    dotColor: "bg-warning",
  },
  all_goals_complete: {
    icon: CupIcon,
    colorClass: "text-secondary",
    dotColor: "bg-secondary",
  },
  goal_locked_in: {
    icon: LockIcon,
    colorClass: "text-primary-300",
    dotColor: "bg-primary-300",
  },
  goal_unlocked: {
    icon: UnlockIcon,
    colorClass: "text-default",
    dotColor: "bg-default",
  },
  comment: {
    icon: QuoteDownCircleIcon,
    colorClass: "text-default-600",
    dotColor: "bg-default-600",
  },
  default: {
    icon: TickCircleIcon,
    colorClass: "text-default",
    dotColor: "bg-default",
  },
};

/**
 * Get icon and color for event type
 * @param {string} type Event type
 * @returns {{icon: React.ComponentType, colorClass: string, dotColor: string}} Style config
 */
function getEventStyle(type) {
  return EVENT_STYLES[type] || EVENT_STYLES.default;
}

/**
 * Format event message based on event type
 * @param {object} event The event object
 * @returns {string|null} Formatted event message (null for comment type)
 */
function formatEventMessage(event) {
  switch (event.type) {
    case "goal_locked_in":
      return "locked in their goals for the week";
    case "goal_unlocked":
      return "unlocked their goals";
    case "goal_checked":
      return `completed: ${event.metadata?.goalText || "a goal"}`;
    case "goal_unchecked":
      return `unchecked: ${event.metadata?.goalText || "a goal"}`;
    case "all_goals_complete":
      return "completed all goals!";
    case "comment":
      return null;
    default:
      return "updated their goals";
  }
}

/**
 * Render comment text with mentions highlighted
 * @param {string} text The comment text containing \@username mentions
 * @returns {React.ReactElement} Rendered comment with highlights
 */
function renderCommentText(text) {
  if (!text) return null;

  const parts = text.split(/(@\w+)/g);
  return parts.map((part, index) => {
    if (part.startsWith("@")) {
      return (
        <span key={index} className="text-primary font-semibold">
          {part}
        </span>
      );
    }
    return part;
  });
}

/**
 * Format timestamp for display
 * @param {Date|object} timestamp Firebase timestamp or Date
 * @returns {string} Formatted time string
 */
function formatTimestamp(timestamp) {
  try {
    // Handle Firebase Timestamp objects
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return "recently";
  }
}

/**
 * Event timeline component for team activity
 * @param {object} props Component props
 * @param {string} props.teamId The team ID for reactions
 * @param {string} props.weekOf The week identifier for comments
 * @param {Array} props.events Array of event objects with user info
 * @param {Array<{userId: string, displayName: string}>} props.teamMembers Team members for mentions
 * @returns {React.ReactElement} Event timeline
 */
export default function EventTimeline({
  teamId,
  weekOf,
  events = [],
  teamMembers = [],
}) {
  const [visibleCount, setVisibleCount] = React.useState(ITEMS_PER_PAGE);
  const scrollContainerRef = React.useRef(null);

  // Create lookup map for team members
  const memberMap = React.useMemo(() => {
    const map = {};
    teamMembers.forEach((m) => {
      map[m.userId] = m;
    });
    return map;
  }, [teamMembers]);

  // Helper to get display name for a user ID
  const getUserDisplayName = (userId) => {
    return memberMap[userId]?.displayName || "Unknown";
  };

  // Reverse events so latest is at bottom
  const reversedEvents = React.useMemo(() => [...events].reverse(), [events]);

  // For display: show last N events (which are at the end after reversal)
  const startIndex = Math.max(0, reversedEvents.length - visibleCount);
  const visibleEvents = reversedEvents.slice(startIndex);
  const hasMore = startIndex > 0;

  // Auto-scroll to bottom when visible events change
  const lastEventId = visibleEvents[visibleEvents.length - 1]?.id;
  React.useEffect(() => {
    if (scrollContainerRef.current && visibleEvents.length > 0) {
      const scrollToBottom = () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop =
            scrollContainerRef.current.scrollHeight;
        }
      };
      // Multiple attempts to ensure scroll happens after render
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
      setTimeout(scrollToBottom, 50);
      setTimeout(scrollToBottom, 150);
    }
  }, [lastEventId, visibleEvents.length]);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  if (events.length === 0) {
    return (
      <div className="flex flex-col">
        <div className="text-default-400 py-8 text-center">
          <p className="text-sm">No activity yet this week</p>
        </div>
        {teamId && weekOf && (
          <CommentInput
            teamId={teamId}
            weekOf={weekOf}
            teamMembers={teamMembers}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div
        ref={scrollContainerRef}
        className="max-h-[28rem] overflow-y-auto lg:max-h-[32rem]"
      >
        {hasMore && (
          <div className="mb-4 text-center">
            <Button size="sm" variant="flat" onPress={handleLoadMore}>
              Load older activity
            </Button>
          </div>
        )}

        <div className="relative">
          {/* Timeline line - extends from first dot to bottom, connecting to comment box */}
          <div className="bg-divider absolute bottom-0 left-[11px] top-3 w-0.5" />

          <div className="space-y-4">
            {visibleEvents.map((event, index) => {
              const {
                icon: Icon,
                colorClass,
                dotColor,
              } = getEventStyle(event.type);
              const isLast = index === visibleEvents.length - 1;

              return (
                <div key={event.id} className="relative flex gap-3">
                  {/* Timeline dot */}
                  <div className="relative z-10 flex size-6 shrink-0 items-center justify-center">
                    <div
                      className={`size-3 rounded-full ${dotColor} ${isLast ? "ring-offset-background ring-2 ring-offset-2" : ""}`}
                    />
                  </div>

                  {/* Event content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                      <Icon
                        className={`mt-0.5 size-4 shrink-0 ${colorClass}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm">
                          <strong className="font-semibold">
                            {getUserDisplayName(event.userId)}
                          </strong>{" "}
                          {event.type === "comment" ? (
                            <span>
                              says {' "'}
                              {renderCommentText(event.metadata?.text)}
                              {'"'}
                            </span>
                          ) : (
                            formatEventMessage(event)
                          )}
                        </p>
                        <time className="text-default-400 text-xs">
                          {formatTimestamp(event.createdAt)}
                        </time>
                        {teamId && (
                          <EventReactions
                            teamId={teamId}
                            eventId={event.id}
                            teamMembers={teamMembers}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {teamId && weekOf && (
        <div className="relative shrink-0">
          {/* Continue the timeline line into the comment area */}
          <div className="bg-divider absolute left-[11px] top-0 h-4 w-0.5" />
          <CommentInput
            teamId={teamId}
            weekOf={weekOf}
            teamMembers={teamMembers}
          />
        </div>
      )}
    </div>
  );
}
